import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import type { ReactNode } from "react"
import type { Verdict } from "../../studio/studioTypes"
import type { Geltung } from "../mapping/geltungMapping"

/**
 * Building blocks of the unit card's header sentence: "300 von 330 Artikeln
 * sind laut Liste konform" / "300 of 330 articles are compliant per article
 * list" (review-mask prototype port). The count pill, the verb, and the
 * sentence-form labels the two selects show in their triggers (the popups
 * keep the full labels). Every token has a long and a short form; the header
 * row (`.sv2-hsent`, studioV2.css) swaps them with a container query so the
 * sentence shrinks instead of breaking onto a second line.
 */

type Translate = (
  key: TranslationKeys,
  v?: Record<string, string | number>,
) => string

/** Long form on a wide row, short form once the row squeezes (CSS-driven,
 *  both are always in the DOM). */
export function LongShort({
  long,
  short,
}: {
  long: ReactNode
  short: ReactNode
}) {
  return (
    <>
      <span className="sv2-hlong">{long}</span>
      <span className="sv2-hshort">{short}</span>
    </>
  )
}

const GELTUNG_SENTENCE_KEY: Record<
  Geltung,
  { long: TranslationKeys; short: TranslationKeys }
> = {
  all: {
    long: "MC_DECL_V2_HEAD_GELTUNG_ALL",
    short: "MC_DECL_V2_HEAD_GELTUNG_ALL_SHORT",
  },
  list: {
    long: "MC_DECL_V2_HEAD_GELTUNG_LIST",
    short: "MC_DECL_V2_HEAD_GELTUNG_LIST_SHORT",
  },
  group: {
    long: "MC_DECL_V2_HEAD_GELTUNG_GROUP",
    short: "MC_DECL_V2_HEAD_GELTUNG_GROUP_SHORT",
  },
  material: {
    long: "MC_DECL_V2_HEAD_GELTUNG_MATERIAL",
    short: "MC_DECL_V2_HEAD_GELTUNG_MATERIAL_SHORT",
  },
  ask: {
    long: "MC_DECL_V2_HEAD_GELTUNG_ASK",
    short: "MC_DECL_V2_HEAD_GELTUNG_ASK_SHORT",
  },
}

const VERDICT_SENTENCE_KEY: Record<Verdict, TranslationKeys> = {
  compliant: "MC_DECL_V2_HEAD_VERDICT_COMPLIANT",
  compliant_with_conditions:
    "MC_DECL_V2_HEAD_VERDICT_COMPLIANT_WITH_CONDITIONS",
  non_compliant: "MC_DECL_V2_HEAD_VERDICT_NON_COMPLIANT",
  pending: "MC_DECL_V2_HEAD_VERDICT_PENDING",
  unknown: "MC_DECL_V2_HEAD_VERDICT_UNKNOWN",
}

/** How the Geltung reads inside the sentence ("… sind laut Liste konform"). */
export function geltungSentence(g: Geltung, translate: Translate): ReactNode {
  const { long, short } = GELTUNG_SENTENCE_KEY[g]
  return <LongShort long={translate(long)} short={translate(short)} />
}

/** The verdict as a predicate ("… konform mit Ausnahme"); a card with no
 *  verdict yet reads "noch offen". */
export function verdictSentence(
  v: Verdict | null,
  translate: Translate,
): string {
  return translate(v ? VERDICT_SENTENCE_KEY[v] : "MC_DECL_V2_HEAD_VERDICT_NONE")
}

/** "ist"/"sind", "is"/"are": agreeing with the count pill's subject. */
export function headVerb(n: number, translate: Translate): string {
  return translate("MC_DECL_V2_HEAD_VERB", { n })
}

export type HeadToken = "count" | "verb" | "geltung" | "verdict"

const DEFAULT_ORDER: HeadToken[] = ["count", "verb", "geltung", "verdict"]

/** The sentence's token order for the current language, read off the
 *  catalog (`MC_DECL_V2_HEAD_ORDER`): German says "sind laut Liste konform",
 *  English "are compliant per article list". Falls back to the German order
 *  when the catalog entry does not name all four tokens exactly once. */
export function headTokenOrder(translate: Translate): HeadToken[] {
  const parsed = translate("MC_DECL_V2_HEAD_ORDER")
    .split(/\s+/)
    .filter((t): t is HeadToken => (DEFAULT_ORDER as string[]).includes(t))
  const complete =
    parsed.length === DEFAULT_ORDER.length &&
    DEFAULT_ORDER.every((t) => parsed.includes(t))
  return complete ? parsed : DEFAULT_ORDER
}

export interface CountPillProps {
  count: number
  total: number
  /** The statement is genuinely scope_all: "Alle 330 Artikel". */
  all?: boolean
  /** An unresolved scope with nothing bound yet: "Unklare Artikel", never
   *  "0 von 330", which would read as "none are compliant". */
  unclear?: boolean
  warn?: boolean
  quiet?: boolean
  title?: string
  /** Renders the pill as a button (the article picker's opener). */
  onClick?: () => void
  translate: Translate
}

/** The subject of the sentence: a pill in the Geltung pill's own shape. */
export function CountPill({
  count,
  total,
  all = false,
  unclear = false,
  warn = false,
  quiet = false,
  title,
  onClick,
  translate,
}: CountPillProps) {
  let long: string
  let short: string
  if (all) {
    // A pool of zero has no number worth saying: fall back to the plain
    // "Alle Artikel" label the Geltung control already uses.
    long =
      total > 0
        ? translate("MC_DECL_V2_HEAD_COUNT_ALL", { total })
        : translate("MC_DECL_V2_GELTUNG_ALL")
    short = translate("MC_DECL_V2_HEAD_COUNT_ALL_SHORT", { total })
  } else if (unclear) {
    long = translate("MC_DECL_V2_HEAD_COUNT_UNCLEAR")
    short = translate("MC_DECL_V2_HEAD_COUNT_UNCLEAR_SHORT")
  } else {
    long = translate("MC_DECL_V2_HEAD_COUNT_OF", { n: count, total })
    short = translate("MC_DECL_V2_HEAD_COUNT_OF_SHORT", { n: count, total })
  }
  const className = cn(
    "sv2-hcount",
    warn && "sv2-hcount-warn",
    quiet && "sv2-hcount-quiet",
  )
  if (!onClick) {
    return (
      <span className={className} title={title} data-testid="count-pill">
        <LongShort long={long} short={short} />
      </span>
    )
  }
  return (
    <button
      type="button"
      className={className}
      title={title}
      onClick={onClick}
      data-testid="count-pill"
    >
      <LongShort long={long} short={short} />
      <span className="sv2-car">▾</span>
    </button>
  )
}

/** The subject's grammatical number for `headVerb`: "Alle 330 Artikel" and
 *  "Unklare Artikel" are plural whatever the count says. */
export function headSubjectCount(opts: {
  count: number
  total: number
  all: boolean
  unclear: boolean
}): number {
  if (opts.all) return opts.total === 1 ? 1 : 2
  if (opts.unclear) return 2
  return opts.count
}
